import React from 'react';
import useContainerSize from '../hooks/useContainerSize';

// Simple Pie Chart Component
export const PieChart = ({ data, width = 200, height = 200, responsive = true }) => {
  const [ref, size] = useContainerSize();
  const finalWidth = responsive && size.width ? Math.min(size.width, width) : width;
  const finalHeight = height; // Keep height constant for readability
  const total = data.reduce((sum, item) => sum + item.value, 0);
  let currentAngle = 0;
  
  const colors = ['var(--expense-red)', '#dc2626', '#b91c1c', '#991b1b', '#7f1d1d', '#fca5a5', '#f87171', '#ef4444'];
  
  return (
    <div ref={responsive ? ref : undefined} style={{ display: 'flex', alignItems: 'center', gap: '1.25rem', flexWrap: 'wrap' }}>
      <svg width={finalWidth} height={finalHeight} viewBox={`0 0 ${finalWidth} ${finalHeight}`}> 
        {data.map((item, index) => {
          const angle = (item.value / total) * 360;
          const startAngle = currentAngle;
          const endAngle = currentAngle + angle;
          
          const x1 = finalWidth/2 + (finalWidth/2 - 10) * Math.cos((startAngle - 90) * Math.PI / 180);
          const y1 = finalHeight/2 + (finalHeight/2 - 10) * Math.sin((startAngle - 90) * Math.PI / 180);
          const x2 = finalWidth/2 + (finalWidth/2 - 10) * Math.cos((endAngle - 90) * Math.PI / 180);
          const y2 = finalHeight/2 + (finalHeight/2 - 10) * Math.sin((endAngle - 90) * Math.PI / 180);
          
          const largeArcFlag = angle > 180 ? 1 : 0;
          
          const pathData = [
            `M ${finalWidth/2} ${finalHeight/2}`,
            `L ${x1} ${y1}`,
            `A ${finalWidth/2 - 10} ${finalHeight/2 - 10} 0 ${largeArcFlag} 1 ${x2} ${y2}`,
            'Z'
          ].join(' ');
          
          currentAngle += angle;
          
          return (
            <path
              key={index}
              d={pathData}
              fill={colors[index % colors.length]}
              stroke="white"
              strokeWidth="2"
            />
          );
        })}
      </svg>
      
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', minWidth: '140px' }}>
        {data.map((item, index) => (
          <div key={index} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.875rem' }}>
            <div style={{ 
              width: '12px', 
              height: '12px', 
              backgroundColor: colors[index % colors.length],
              borderRadius: '2px'
            }} />
            <span>{item.name}: {((item.value / total) * 100).toFixed(1)}%</span>
          </div>
        ))}
      </div>
    </div>
  );
};

// Simple Bar Chart Component
export const BarChart = ({ data, width = 400, height = 220, formatCurrency, responsive = true, chartType = 'expense' }) => {
  const [ref, size] = useContainerSize();
  
  // Use full container width when responsive
  const effectiveWidth = responsive && size.width ? size.width : width;
  const maxValue = Math.max(...data.map(item => item.value));
  
  // Better bar spacing calculation - use more of the available width
  const chartPadding = 60;
  const barSpacing = 8;
  const availableWidth = effectiveWidth - chartPadding;
  const totalBarSpacing = barSpacing * (data.length - 1);
  const barWidth = Math.max((availableWidth - totalBarSpacing) / data.length, 40);
  
  // Enhanced height for better visibility
  const chartHeight = height;
  const barAreaHeight = chartHeight - 60;
  
  if (!data || data.length === 0) {
    return (
      <div style={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center', 
        height: chartHeight + 'px',
        color: 'var(--gray-500)',
        fontSize: '0.875rem'
      }}>
        {chartType === 'expense' ? 'No expense data available' : 'No income data available'}
      </div>
    );
  }
  
  return (
    <div ref={responsive ? ref : undefined} style={{ width: '100%' }}>
      <svg width="100%" height={chartHeight + 50} viewBox={`0 0 ${effectiveWidth} ${chartHeight + 50}`} style={{ overflow: 'visible' }}>
        {/* Gradient Definition */}
        <defs>
          <linearGradient id="expenseGradient" x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" style={{ stopColor: 'var(--expense-red)', stopOpacity: 0.9 }} />
            <stop offset="50%" style={{ stopColor: 'var(--expense-red)', stopOpacity: 0.7 }} />
            <stop offset="100%" style={{ stopColor: 'var(--expense-red)', stopOpacity: 0.5 }} />
          </linearGradient>
          <linearGradient id="incomeGradient" x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" style={{ stopColor: 'var(--income-green)', stopOpacity: 0.9 }} />
            <stop offset="50%" style={{ stopColor: 'var(--income-green)', stopOpacity: 0.7 }} />
            <stop offset="100%" style={{ stopColor: 'var(--income-green)', stopOpacity: 0.5 }} />
          </linearGradient>
        </defs>
        
        {/* Y-axis */}
        <line x1="40" y1="20" x2="40" y2={chartHeight - 20} stroke="#e5e7eb" strokeWidth="1" />
        
        {/* X-axis */}
        <line x1="40" y1={chartHeight - 20} x2={effectiveWidth - 20} y2={chartHeight - 20} stroke="#e5e7eb" strokeWidth="1" />
        
        {/* Bars */}
        {data.map((item, index) => {
          const barHeight = maxValue > 0 ? (item.value / maxValue) * barAreaHeight : 0;
          const x = 50 + index * (barWidth + barSpacing);
          const y = chartHeight - 20 - barHeight;
          
          return (
            <g key={index}>
              {/* Bar */}
              <rect
                x={x}
                y={y}
                width={barWidth}
                height={barHeight}
                fill={chartType === 'expense' ? "url(#expenseGradient)" : "url(#incomeGradient)"}
                rx="4"
                ry="4"
              />
              
              {/* Category Label */}
              <text
                x={x + barWidth / 2}
                y={chartHeight + 5}
                textAnchor="middle"
                fontSize="11"
                fill="#6b7280"
                fontWeight="500"
              >
                {item.name.length > 10 ? item.name.substring(0, 10) + '...' : item.name}
              </text>
              
              {/* Value Label */}
              <text
                x={x + barWidth / 2}
                y={y - 8}
                textAnchor="middle"
                fontSize="10"
                fill="#374151"
                fontWeight="600"
              >
                {formatCurrency ? formatCurrency(item.value) : `₹${item.value}`}
              </text>
            </g>
          );
        })}
      </svg>
      
      {/* Type Indicator */}
      <div style={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center', 
        marginTop: '0.75rem',
        fontSize: '0.75rem',
        color: 'var(--gray-600)'
      }}>
        <div style={{
          width: '12px',
          height: '12px',
          borderRadius: '2px',
          background: chartType === 'expense' ? 'var(--expense-red)' : 'var(--income-green)',
          marginRight: '0.5rem'
        }}></div>
        <span style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <span className="material-icons" style={{ fontSize: '14px' }}>
            {chartType === 'expense' ? 'trending_down' : 'trending_up'}
          </span>
          {chartType === 'expense' ? 'Expense Categories' : 'Income Sources'}
        </span>
      </div>
    </div>
  );
};

// Enhanced Line Chart Component
export const LineChart = ({ data, width = 400, height = 200, formatCurrency, responsive = true, scrollableMode = false, pointWidth = 60 }) => {
  const [ref, size] = useContainerSize();
  
  // Scrollable mode: calculate width based on data points for horizontal scrolling
  const calculatedWidth = scrollableMode ? Math.max(width, data.length * pointWidth + 160) : width;
  const effectiveWidth = responsive && size.width ? Math.min(size.width, calculatedWidth) : calculatedWidth;
  if (!data || data.length === 0) {
    return (
      <div style={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center', 
        height: '300px',
        color: 'var(--gray-500)',
        fontSize: '1rem'
      }}>
        No trend data available
      </div>
    );
  }
  
  const maxValue = Math.max(...data.map(item => Math.max(item.income || 0, item.expenses || 0)));
  if (maxValue === 0) {
    return (
      <div style={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center', 
        height: '300px',
        color: 'var(--gray-500)',
        fontSize: '1rem'
      }}>
        No financial data to display
      </div>
    );
  }
  
  const padding = { left: 80, right: 40, top: 40, bottom: 80 };
  const chartWidth = effectiveWidth - padding.left - padding.right;
  const chartHeight = height - padding.top - padding.bottom;
  const stepX = data.length > 1 ? chartWidth / (data.length - 1) : 0;
  
  const defaultFormatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };
  
  const currencyFormatter = formatCurrency || defaultFormatCurrency;
  
  const getY = (value) => {
    if (isNaN(value) || value === null || value === undefined) return padding.top + chartHeight;
    return padding.top + chartHeight - ((value / maxValue) * chartHeight);
  };
  
  // Adjust getX for scrollable area (no left padding since Y-axis is separate)
  const getX = (index) => 20 + index * stepX;
  
  // Generate smooth curve paths
  const generateSmoothPath = (points) => {
    if (points.length < 2) return '';
    
    let path = `M ${points[0].x} ${points[0].y}`;
    
    for (let i = 1; i < points.length; i++) {
      const prev = points[i - 1];
      const curr = points[i];
      const next = points[i + 1];
      
      if (i === 1) {
        const cp1x = prev.x + (curr.x - prev.x) * 0.3;
        const cp1y = prev.y;
        const cp2x = curr.x - (curr.x - prev.x) * 0.3;
        const cp2y = curr.y;
        path += ` C ${cp1x} ${cp1y}, ${cp2x} ${cp2y}, ${curr.x} ${curr.y}`;
      } else {
        const cp1x = prev.x + (curr.x - (points[i - 2]?.x || prev.x)) * 0.2;
        const cp1y = prev.y + (curr.y - (points[i - 2]?.y || prev.y)) * 0.2;
        const cp2x = curr.x - (next?.x || curr.x - prev.x) * 0.2;
        const cp2y = curr.y - (next?.y || curr.y - prev.y) * 0.2;
        path += ` C ${cp1x} ${cp1y}, ${cp2x} ${cp2y}, ${curr.x} ${curr.y}`;
      }
    }
    
    return path;
  };
  
  const incomePoints = data.map((item, index) => ({
    x: getX(index),
    y: getY(item.income || 0)
  }));
  
  const expensePoints = data.map((item, index) => ({
    x: getX(index),
    y: getY(item.expenses || 0)
  }));
  
  const incomePath = generateSmoothPath(incomePoints);
  const expensePath = generateSmoothPath(expensePoints);
  
  // Calculate fixed Y-axis width and scrollable chart width
  const yAxisWidth = padding.left;
  const scrollableChartWidth = scrollableMode ? Math.max(width - yAxisWidth, data.length * pointWidth + padding.right) : width - yAxisWidth;
  const totalHeight = height + 60;

  return (
    <div ref={responsive ? ref : undefined} style={{ width: '100%', display: 'flex', position: 'relative' }}>
      {/* Fixed Y-axis */}
      <div style={{ width: yAxisWidth, flexShrink: 0, position: 'relative', zIndex: 2 }}>
        <svg width={yAxisWidth} height={totalHeight} viewBox={`0 0 ${yAxisWidth} ${totalHeight}`} style={{ display: 'block' }}>
          <defs>
            <filter id="yAxisShadow">
              <feDropShadow dx="1" dy="0" stdDeviation="1" floodOpacity="0.1"/>
            </filter>
          </defs>
          
          {/* Y-axis line */}
          <line
            x1={yAxisWidth - 1}
            y1={padding.top}
            x2={yAxisWidth - 1}
            y2={padding.top + chartHeight}
            stroke="#e5e7eb"
            strokeWidth="1"
          />
          
          {/* Y-axis labels */}
          {[0, 0.2, 0.4, 0.6, 0.8, 1].map((ratio, index) => (
            <g key={`y-axis-${index}`}>
              <line
                x1={yAxisWidth - 5}
                y1={padding.top + ratio * chartHeight}
                x2={yAxisWidth - 1}
                y2={padding.top + ratio * chartHeight}
                stroke="#d1d5db"
                strokeWidth="1"
              />
              <text
                x={yAxisWidth - 10}
                y={padding.top + ratio * chartHeight + 4}
                textAnchor="end"
                fontSize="11"
                fill="#64748b"
                fillOpacity="0.8"
                fontWeight="500"
              >
                {currencyFormatter(maxValue - (ratio * maxValue))}
              </text>
            </g>
          ))}
        </svg>
      </div>

      {/* Scrollable chart area */}
      <div style={{ 
        flex: 1, 
        overflowX: scrollableMode ? 'auto' : 'hidden', 
        overflowY: 'hidden',
        WebkitOverflowScrolling: 'touch'
      }}>
        <svg width={scrollableChartWidth + padding.right} height={totalHeight} viewBox={`0 0 ${scrollableChartWidth + padding.right} ${totalHeight}`}>
      <defs>
        {/* Professional transparent gradients for line chart */}
        <linearGradient id="incomeLineGradient" x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor="var(--income-green)" stopOpacity="0.15" />
          <stop offset="50%" stopColor="var(--income-green)" stopOpacity="0.08" />
          <stop offset="100%" stopColor="var(--income-green)" stopOpacity="0.02" />
        </linearGradient>
        <linearGradient id="expenseLineGradient" x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor="var(--expense-red)" stopOpacity="0.15" />
          <stop offset="50%" stopColor="var(--expense-red)" stopOpacity="0.08" />
          <stop offset="100%" stopColor="var(--expense-red)" stopOpacity="0.02" />
        </linearGradient>
        {/* Enhanced shadow for professional look */}
        <filter id="professionalShadow">
          <feDropShadow dx="0" dy="1" stdDeviation="2" floodOpacity="0.15"/>
        </filter>
        <filter id="lineGlow">
          <feDropShadow dx="0" dy="0" stdDeviation="1" floodOpacity="0.3"/>
        </filter>
      </defs>
      
      {/* Horizontal grid lines (without Y-axis labels since they're separate) */}
      {[0, 0.2, 0.4, 0.6, 0.8, 1].map((ratio, index) => (
        <line
          key={`grid-${index}`}
          x1={0}
          y1={padding.top + ratio * chartHeight}
          x2={scrollableChartWidth}
          y2={padding.top + ratio * chartHeight}
          stroke="#f1f5f9"
          strokeWidth="0.5"
          strokeOpacity="0.6"
          strokeDasharray="3,3"
        />
      ))}
      
      {/* Subtle vertical grid lines */}
      {data.map((item, index) => (
        <line
          key={`vgrid-${index}`}
          x1={getX(index)}
          y1={padding.top}
          x2={getX(index)}
          y2={padding.top + chartHeight}
          stroke="#f8fafc"
          strokeWidth="0.5"
          strokeOpacity="0.4"
        />
      ))}
      
      {/* Professional transparent area fills */}
      <path
        d={`${incomePath} L ${getX(data.length - 1)} ${padding.top + chartHeight} L ${padding.left} ${padding.top + chartHeight} Z`}
        fill="url(#incomeLineGradient)"
      />
      <path
        d={`${expensePath} L ${getX(data.length - 1)} ${padding.top + chartHeight} L ${padding.left} ${padding.top + chartHeight} Z`}
        fill="url(#expenseLineGradient)"
      />
      
      {/* Professional trend lines with enhanced styling */}
      <path
        d={incomePath}
        fill="none"
        stroke="var(--income-green)"
        strokeWidth="2.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeOpacity="0.9"
        filter="url(#professionalShadow)"
      />
      <path
        d={expensePath}
        fill="none"
        stroke="var(--expense-red)"
        strokeWidth="2.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeOpacity="0.9"
        filter="url(#professionalShadow)"
      />
      
      {/* Professional data points with transparency */}
      {data.map((item, index) => {
        const incomeY = getY(item.income || 0);
        const expensesY = getY(item.expenses || 0);
        const x = getX(index);
        
        return (
          <g key={`points-${index}`}>
            {/* Income data point */}
            <circle
              cx={x}
              cy={incomeY}
              r="4"
              fill="var(--income-green)"
              fillOpacity="0.8"
              stroke="white"
              strokeWidth="2"
              strokeOpacity="0.9"
              filter="url(#professionalShadow)"
            />
            {/* Expense data point */}
            <circle
              cx={x}
              cy={expensesY}
              r="4"
              fill="var(--expense-red)"
              fillOpacity="0.8"
              stroke="white"
              strokeWidth="2"
              strokeOpacity="0.9"
              filter="url(#professionalShadow)"
            />
            {/* Period label */}
            <text
              x={x}
              y={padding.top + chartHeight + 25}
              textAnchor="middle"
              fontSize="11"
              fill="#64748b"
              fillOpacity="0.8"
              fontWeight="500"
            >
              {item.period || ''}
            </text>
          </g>
        );
      })}
      </svg>
    </div>
    </div>
  );
};